import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, WifiOff, RefreshCw } from 'lucide-react';

export const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { user, setUser, checkAuth, setLoading, backendUnreachable } = useAuth();
  const [authState, setAuthState] = useState(
    location.state?.authenticated ? 'authenticated' : 'checking'
  );
  const hasChecked = useRef(false);

  useEffect(() => {
    // If user data was passed from AuthCallback, use it directly and skip check
    if (location.state?.authenticated && location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      setAuthState('authenticated');
      // Clear the location state to prevent re-use
      window.history.replaceState({}, '', location.pathname);
      return;
    }

    // If already have user in context, skip check
    if (user) {
      setAuthState('authenticated');
      return;
    }

    // Check if we have a token in localStorage - if so, we should be authenticated
    const token = localStorage.getItem('gs_session_token');
    if (!token) {
      setAuthState('unauthenticated');
      return;
    }

    // Prevent double checking
    if (hasChecked.current) return;
    hasChecked.current = true;

    // Otherwise, check auth with server. checkAuth() already retries with
    // exponential backoff on transient errors (5xx / network / Cloudflare
    // 520-524). If it returns null AFTER its retry budget AND the backend
    // is flagged as unreachable, stay in 'checking' state — the splash
    // below will rotate to the "Reconnecting" UI instead of dumping the
    // operator on the login page during a transient outage.
    const verifyAuth = async () => {
      const userData = await checkAuth();
      if (userData) {
        setAuthState('authenticated');
        return;
      }
      // No user, AND we still have a token in storage → assume infra blip
      // until something concrete (explicit 401) clears the token for us.
      if (localStorage.getItem('gs_session_token')) {
        setAuthState('checking');
        // Schedule another verification attempt in 15s so the user picks
        // up automatically as soon as the backend recovers.
        setTimeout(() => {
          hasChecked.current = false;
          verifyAuth();
        }, 15000);
        return;
      }
      setAuthState('unauthenticated');
    };

    verifyAuth();
  }, [location.state, location.pathname, checkAuth, setUser, setLoading, user]);

  // Backend unreachable splash — replaces the silent "kicked to login"
  // behaviour during pod restarts / Cloudflare 5xx outages.
  if (authState === 'checking' && backendUnreachable) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        data-testid="auth-reconnecting-splash"
      >
        <div className="text-center max-w-md px-6">
          <div className="relative inline-flex items-center justify-center">
            <WifiOff className="w-10 h-10 text-amber-500" />
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground absolute -bottom-1 -right-1" />
          </div>
          <p className="mt-5 text-base font-medium">Reconnecting to server…</p>
          <p className="mt-2 text-sm text-muted-foreground">
            We're holding your session. You'll be back in as soon as the server responds.
          </p>
        </div>
      </div>
    );
  }

  // Still checking
  if (authState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (authState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  // Authenticated
  return children;
};
